import React, {useEffect, useState} from 'react'
import {KTCard, KTCardBody, KTIcon} from "../../../_websoft/helpers"
import {ToastContainer} from "react-toastify"
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    SortingState,
    ColumnFiltersState,
    useReactTable
} from "@tanstack/react-table"

import {
    getPanelDopItems,
    deletePanelDopItem
} from "../../api/paneliDop"

import notifyToast from "../../components/Toast/Toast"
import {TableActionButton} from "../../components/TableActionBtn/TableActionButton"
import {Link, useNavigate} from "react-router-dom"
import TableSearch from "../../components/TableSearch/TableSearch"
import ExportButton from "../../components/TableExportToExcel/ExportButton"
import LoadingComponent from "../../components/LoadingComponent/LoadingComponent"
import TablePagination from "../../components/TablePagination/TablePagination"

import {DigitalniDopItemValues} from "../../models/DigitalniDopModel"
import {PaginationModel} from "../../models/PaginationModel"

const DigitalniDopStavke = () => {

    const [data, setData] = useState<DigitalniDopItemValues[]>([])
    const [loading, setLoading] = useState(true)

    const [params, setParams] = useState({per_page: 10, page: 1})
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

    const [pagination, setPagination] = useState<PaginationModel>({
        curPage: 1,
        totalPage: 1,
        perPage: 10,
    })

    const [sorting, setSorting] = useState<SortingState>([])
    const [globalFilter, setGlobalFilter] = useState('')
    const [rowSelection, setRowSelection] = useState({})

    /**
     * LOAD DATA
     */
    const updatePage = () => {
        setLoading(true)

        getPanelDopItems(params)
            .then((response) => {
                const res = response.data

                setData(res.data || [])
                console.log(data, 'data');
                const totalPage = Math.ceil(res.total / res.per_page)

                setPagination({
                    curPage: res.current_page,
                    totalPage: totalPage,
                    perPage: res.per_page,
                })
            })
            .catch((error) => {
                console.error('Error: ', error)
                notifyToast(false, 'Greška prilikom učitavanja')
            })
            .finally(() => {
                setLoading(false)
                setRowSelection({})
            })
    }

    useEffect(() => {
        updatePage()
    }, [params])

    /**
     * DELETE
     */
    const onDelete = (id?: number) => {
        if (!id) return

        deletePanelDopItem(id)
            .then((response) => {
                updatePage()
                notifyToast(true, response.data.message)
            })
            .catch(() => {
                notifyToast(false, 'Greška prilikom brisanja')
            })
    }

    /**
     * NAVIGATION
     */
    const navigate = useNavigate()

    const onEdit = (id?: number) => {
        if (!id) return
        navigate(`/paneli/digitalni-dop-stavke/izmjena/${id}`)
    }

    /**
     * COLUMNS
     */
    const columns = [
        {header: 'Id', accessorKey: 'Id'},
        {header: 'Numeracija', accessorKey: 'Numeracija'},
        {header: 'Naziv', accessorKey: 'Naziv'},
        {header: 'Naziv EN', accessorKey: 'NazivEng'},
        {header: 'Naziv DE', accessorKey: 'NazivDe'},
        {header: 'Naziv ES', accessorKey: 'NazivEs'},
        {header: 'Naziv SL', accessorKey: 'NazivSl'},
        {header: 'Mjera', accessorKey: 'Mjera'},
        {header: 'Standard', accessorKey: 'Standard'},
        {
            header: 'Akcija',
            cell: ({row}) => (
                <TableActionButton
                    onDelete={() => onDelete(row.original.Id)}
                    onEdit={() => onEdit(row.original.Id)}
                />
            ),
        },
    ]

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            globalFilter,
            columnFilters,
            rowSelection,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        onColumnFiltersChange: setColumnFilters,
        onRowSelectionChange: setRowSelection,
        enableRowSelection: true,
    })

    return (
        <KTCard>
            <ToastContainer />

            <KTCardBody className='py-4'>
                <div className='d-flex justify-content-between'>
                    <TableSearch onSearch={(text) => setGlobalFilter(text)}/>

                    <div>
                        <ExportButton table={table} filename={'dop-stavke'}/>

                        <Link to='/paneli/digitalni-dop-stavke/dodaj'>
                            <button type='button' className='btn btn-primary'>
                                <KTIcon iconName='plus' className='fs-2'/>
                                Dodaj
                            </button>
                        </Link>
                    </div>
                </div>

                {loading ? (
                    <LoadingComponent/>
                ) : (
                    <div className='table-responsive mt-3'>
                        <table className='table align-middle table-bordered gy-5'>
                            <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id} className='bg-dark text-white'>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            className="text-white bg-dark"
                                            onClick={header.column.getToggleSortingHandler()}
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {{
                                                asc: ' 🔼',
                                                desc: ' 🔽',
                                            }[header.column.getIsSorted() as string] ?? null}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>

                            <tbody>
                            {table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map((row) => (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={10} className='text-center'>
                                        No matching records found
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                )}

                <TablePagination
                    updatePage={(page, per_page) =>
                        setParams({...params, page, per_page})
                    }
                    pagination={pagination}
                />
            </KTCardBody>
        </KTCard>
    )
}

export default DigitalniDopStavke